import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from 'tea-component';
import { getStudiosPermission } from '@sg/_config/permission';
import { getBoardGovernanceStudios } from '@sg/services/studio';
import type { BoardGovernanceStudioOption } from '@sg/services/studio';
import {
  BoardGovernanceTab,
  SGBuriedBtnPoint,
  createBtnPoint,
} from '@sg/utils/buriedPoint';
import { sortByAlphaNumericText } from '@sg/utils/common';

const BOARD_GOVERNANCE_PAGE_ID = 'studio_boardGovernance';
const STUDIO_GROUP_KEY = 'separated';

interface ITabAuthItem {
  value: string;
  hasAuth?: boolean;
}

interface IUseBoardGovernanceStudiosParams {
  activeSegment: string;
  permissionReady: boolean;
  tabList: ITabAuthItem[];
}

interface ISelectOption {
  text: string;
  label: string;
  value: string;
  groupKey: string;
}

const toStudioIdsKey = (ids: string[]): string => [...ids].sort().join(',');

/**
 * Board Governance studio filter state (tab-bar SelectMultiple).
 * Select All / Clear All update the open panel only; meetings fetch commits on close
 * (same timing as CascadeProjectCustom / default GPSelectMultiple checkbox close).
 */
export const useBoardGovernanceStudios = ({
  activeSegment,
  permissionReady,
  tabList,
}: IUseBoardGovernanceStudiosParams) => {
  const [studioOptions, setStudioOptions] = useState<BoardGovernanceStudioOption[]>([]);
  const [selectedStudioIds, setSelectedStudioIds] = useState<string[]>([]);
  const [studioIds, setStudioIds] = useState<string[]>([]);
  // Select All / Clear All defer commit until close (onChange may be skipped when value already matches).
  const pendingCommitIdsRef = useRef<string[] | null>(null);

  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const hasBoardGovernanceView = useMemo(
    () => tabList.find(item => item.value === 'boardGovernance')?.hasAuth ?? false,
    [tabList],
  );

  const isBoardGovernanceTabReady = useMemo(
    () => tabList.some(item => item.value === 'boardGovernance'),
    [tabList],
  );

  const commitStudioIds = useCallback((nextIds: string[]) => {
    setSelectedStudioIds((prev) => {
      if (toStudioIdsKey(prev) === toStudioIdsKey(nextIds)) {
        return prev;
      }

      const btnPoint = createBtnPoint(
        BoardGovernanceTab,
        'studioFilter',
        `Studio Filter - ${nextIds.length} selected`,
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }

      return nextIds;
    });
  }, []);

  const syncStudioIds = useCallback((nextIds: string[]) => {
    pendingCommitIdsRef.current = null;
    setStudioIds(nextIds);
    setSelectedStudioIds(nextIds);
  }, []);

  useEffect(() => {
    if (!permissionReady || !isBoardGovernanceTabReady) {
      return;
    }

    if (!hasBoardGovernanceView) {
      setStudioOptions([]);
      syncStudioIds([]);
      setOptionsLoading(false);
      setOptionsLoaded(false);
      return;
    }

    if (activeSegment !== 'boardGovernance' || optionsLoaded) {
      return;
    }

    let cancelled = false;

    const fetchStudioOptions = async () => {
      try {
        setOptionsLoading(true);
        // API shape: { data: { studios: [{ id, name, cover }] } } (rawData: true).
        const response = await getBoardGovernanceStudios();
        const rawStudios = response?.data?.studios;
        const permittedStudios = (Array.isArray(rawStudios) ? rawStudios : [])
          .filter((studio: BoardGovernanceStudioOption) => (
            studio?.id && getStudiosPermission(BOARD_GOVERNANCE_PAGE_ID, studio.id)
          ));
        const sortedStudios = sortByAlphaNumericText(
          permittedStudios,
          (studio) => studio.name || '',
        );

        if (cancelled) {
          return;
        }

        setStudioOptions(sortedStudios);
        // Select every permitted studio initially; the API still receives real studio IDs.
        syncStudioIds(sortedStudios.map((studio) => studio.id));
        setOptionsLoaded(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Failed to fetch Board Governance studio options:', error);
        notification.error({ description: 'Failed to fetch studio options' });
        setStudioOptions([]);
        syncStudioIds([]);
        setOptionsLoaded(false);
      } finally {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      }
    };

    void fetchStudioOptions();

    return () => {
      cancelled = true;
    };
  }, [
    activeSegment,
    optionsLoaded,
    hasBoardGovernanceView,
    isBoardGovernanceTabReady,
    permissionReady,
    syncStudioIds,
  ]);

  const selectOptions = useMemo<ISelectOption[]>(
    () => studioOptions.map((studio) => ({
      text: studio.name,
      label: studio.name,
      value: studio.id,
      groupKey: STUDIO_GROUP_KEY,
    })),
    [studioOptions],
  );

  const allStudioIds = useMemo(
    () => selectOptions.map((option) => option.value),
    [selectOptions],
  );

  const isAllSelected = selectOptions.length > 0
    && studioIds.length === selectOptions.length;

  // Same as gpop MultipleSelector: empty selection means "all studios".
  const resolveStudioIds = useCallback((nextIds: string[]) => {
    if (!nextIds.length && allStudioIds.length > 0) {
      return allStudioIds;
    }
    return nextIds;
  }, [allStudioIds]);

  // Checkbox path: GPSelectMultiple emits onChange on close — commit immediately with those ids.
  // Empty → restore all (gpop MultipleSelector onClose).
  const handleStudioChange = useCallback((nextIds: string[]) => {
    pendingCommitIdsRef.current = null;
    const resolvedIds = resolveStudioIds(nextIds);
    setStudioIds(resolvedIds);
    commitStudioIds(resolvedIds);
  }, [commitStudioIds, resolveStudioIds]);

  // Select All / Clear All: panel only; commit on close (Cascade-style).
  const handleToggleSelectAll = useCallback(() => {
    const nextIds = isAllSelected ? [] : allStudioIds;
    pendingCommitIdsRef.current = nextIds;
    setStudioIds(nextIds);
  }, [allStudioIds, isAllSelected]);

  const handleStudioClose = useCallback(() => {
    if (!pendingCommitIdsRef.current) {
      return;
    }
    // Clear All + click outside → checkboxes auto-select back to all (gpop).
    const resolvedIds = resolveStudioIds(pendingCommitIdsRef.current);
    setStudioIds(resolvedIds);
    commitStudioIds(resolvedIds);
    pendingCommitIdsRef.current = null;
  }, [commitStudioIds, resolveStudioIds]);

  const selectGroups = useMemo(
    () => ({
      [STUDIO_GROUP_KEY]: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Studio</span>
          {selectOptions.length > 0 && (
            <span
              className="dbg-text-highlight dbg-cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleSelectAll();
              }}
            >
              {isAllSelected ? 'Clear All' : 'Select All'}
            </span>
          )}
        </div>
      ),
    }),
    [handleToggleSelectAll, isAllSelected, selectOptions.length],
  );

  // allOption={false} drops the default trigger "All" label; restore full-select display here.
  // Empty → gray "All" via gpop placeholder class; explicit Select All → normal value color.
  const selectButton = useCallback((selectedOptions: Array<{ text?: string }>) => {
    const selectedCount = selectedOptions?.length || 0;
    const isDeselectedAll = selectedCount === 0;
    const isExplicitAll = selectOptions.length > 0
      && selectedCount === selectOptions.length;

    return (
      <div className="dbg-common-select-multiple-button">
        <span className="select-button-text">Studio</span>
        {isDeselectedAll && (
          <span className="dbg-common-select-placeholder">All</span>
        )}
        {isExplicitAll && <span className="select-button-value">All</span>}
        {!isDeselectedAll && !isExplicitAll && selectedCount > 1 && (
          <span className="select-button-value">{selectedCount}</span>
        )}
        {!isDeselectedAll && !isExplicitAll && selectedCount === 1 && (
          <span className="select-button-value">{selectedOptions[0]?.text}</span>
        )}
      </div>
    );
  }, [selectOptions.length]);

  return {
    selectedStudioIds,
    studioIds,
    optionsLoading,
    selectOptions,
    selectGroups,
    selectButton,
    handleStudioChange,
    handleStudioClose,
  };
};
